import { useEffect, useState } from 'react'
import { api, type StornoBild } from '../api'
import { anzahl, datumKurz, euro, prozent, stunde } from '../format'
import { Anteile } from '../komponenten/Anteile'
import { Balken } from '../komponenten/Balken'
import { Karte } from '../komponenten/Karte'
import { stundenLuecken } from '../komponenten/diagramm'
import { Kennzahl } from '../komponenten/Kennzahl'

/* Stornos gibt es in beiden Werkzeugen, aber nicht dieselben.
 *
 * Im Reporting steht, wie viel storniert wurde, woraufhin und wann.
 * Im Audit steht zusätzlich, wer es getan hat — und das ist kein
 * Ausblenden: Das Reporting fragt den Personenteil gar nicht erst an
 * (`personen=false`), also verlässt er den Server nicht. Der Schnitt
 * zwischen den Modulen ist der Schnitt beim Personenbezug.
 */

export function Stornos({ von, bis, betriebe, modul = 'reporting' }: {
  von: string; bis: string; betriebe: string[]
  modul?: 'reporting' | 'audit'
}) {
  const [d, setD] = useState<StornoBild | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)
  const mitPersonen = modul === 'audit'

  useEffect(() => {
    let weg = false
    setLaedt(true); setFehler(null)
    api.stornoreport(von, bis, betriebe, mitPersonen)
      .then((x) => { if (!weg) setD(x) })
      .catch((e) => { if (!weg) setFehler(e.message) })
      .finally(() => { if (!weg) setLaedt(false) })
    return () => { weg = true }
  }, [von, bis, betriebe.join(','), mitPersonen])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (laedt && !d) return <div className="laedt">Wird geladen …</div>
  if (!d) return null

  // Die Frage des Audits, deshalb steht sie dort oben und nicht
  // unten. Im Reporting wird sie nicht gestellt.
  const werStorniert = (
  <Karte titel="Wer storniert" neben="die ausführende Person, nicht der Tischinhaber">
    {!d.mit_personen ? (
      <div className="leer" style={{ maxWidth: '62ch', lineHeight: 1.7 }}>
        Für diesen Mandanten ist die Bedienerdimension nicht
        eingeschaltet. Stornos werden gezählt, aber keiner Person
        zugeordnet — und die Namen sind beim Import verworfen worden,
        bevor sie irgendwo standen.
      </div>
    ) : (
      <>
        <table>
          <thead>
            <tr>
              <th>Kennung</th>
              <th className="r">Stornos</th>
              <th className="r">Anteil</th>
              <th className="r">Summe</th>
              <th className="r">nach Wiederöffnung</th>
              <th className="r">Belege</th>
              <th className="r">für … Eröffner</th>
            </tr>
          </thead>
          <tbody>
            {d.personen.map((p) => (
              <tr key={p.person_id}>
                <td>
                  <span className="zahl">{p.kennung}</span>
                  {p.herkunft === 'REPORT' && (
                    <span className="dezent" style={{ marginLeft: 7, fontSize: 11 }}>
                      nur im Bericht
                    </span>
                  )}
                </td>
                <td className="r zahl">{anzahl(p.stornos)}</td>
                <td className="r zahl dezent">{prozent(p.anteil)}</td>
                <td className="r zahl">{euro(p.summe)}</td>
                <td className="r zahl">{anzahl(p.wiedereroeffnet)}</td>
                <td className="r zahl dezent">{anzahl(p.belege)}</td>
                <td className="r zahl dezent">{anzahl(p.fuer_eroeffner)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="dezent" style={{ fontSize: 11, marginTop: 12, marginBottom: 0, lineHeight: 1.6, maxWidth: '78ch' }}>
          Gezählt wird, wer storniert hat — nicht, wem der Tisch
          gehört. Beides steht im Bericht, und es sind fast nie
          dieselben Personen. Eine hohe Zahl ist für sich kein
          Vorwurf: wer abkassiert und Schichten leitet, storniert für
          andere mit. Deshalb steht daneben, für wie viele
          verschiedene Eröffner das geschah.
        </p>
      </>
    )}
  </Karte>
  )

  const k = d.kopf
  const mehrereStandorte = new Set(d.belege.map((b) => b.betrieb_id)).size > 1

  const kopf = (
    <>
      <p style={{ margin: '0 0 14px', lineHeight: 1.6 }}>
        Stornoreport liegt für {anzahl(k.quelltage)} von {anzahl(k.tage_gesamt)} Tagen vor.
        {' '}Ein gültiger, aber leerer Stornoreport trägt keinen Geschäftstag —
        „kein Bericht“ und „nichts storniert“ sind an einem Tag ohne
        Stornozeilen nicht zu unterscheiden.
      </p>
      <div className="gitter g-4" style={{ marginBottom: 14 }}>
        <Kennzahl etikett="Stornos" wert={anzahl(k.stornos)}
                  fuss={k.tage > 1 ? `an ${k.tage} Tagen` : undefined} />
        <Kennzahl etikett="Summe" wert={euro(k.summe)}
                  fuss={k.ohne_preis
                    ? `${anzahl(k.ohne_preis)} ohne eigenen Preis`
                    : undefined} />
        <Kennzahl etikett="Je 100 Rechnungen"
                  wert={k.je_hundert_rechnungen !== null
                    ? prozent(k.je_hundert_rechnungen).replace(' %', '')
                    : 'nicht auswertbar'}
                  fuss={k.je_hundert_rechnungen !== null
                    ? 'vergleichbar zwischen Standorten'
                    : 'keine ableitbare Stornoquelle mit Rechnungen'} />
        <Kennzahl etikett="Nach Wiedereröffnung"
                  wert={anzahl(k.wiedereroeffnet)}
                  fuss={k.quote_wiedereroeffnet !== null
                    ? `${prozent(k.quote_wiedereroeffnet)} aller Stornos`
                    : undefined} />
      </div>
    </>
  )

  if (!d.vorhanden) {
    return (
      <>
        {kopf}
        <div className="karte"><div className="leer" style={{ maxWidth: '62ch', margin: '0 auto', lineHeight: 1.7 }}>
          <b style={{ color: 'var(--schrift)' }}>Für diesen Zeitraum liegt kein Stornoreport vor.</b>
          <div style={{ marginTop: 10 }}>
            Das Kassenarchiv zählt Stornos, aber es sagt nicht, warum
            storniert wurde und wer es getan hat. Beides steht nur in
            diesem zweiten Bericht.
          </div>
        </div></div>
      </>
    )
  }

  return (
    <>
      {kopf}
      {mitPersonen && <div style={{ marginBottom: 14 }}>{werStorniert}</div>}

      <div className="gitter g-2" style={{ marginBottom: 14 }}>
        <Karte titel="Stornogründe" neben="Auswahl an der Kasse">
          <Anteile
            anteile={d.gruende.map((g) => ({ name: g.grund, wert: g.anzahl }))}
            formatiere={(v) => anzahl(v)} />
          <table style={{ marginTop: 14 }}>
            <thead>
              <tr>
                <th>Grund</th>
                <th className="r">Anzahl</th>
                <th className="r">Summe</th>
                <th className="r">nach Wiederöffnung</th>
              </tr>
            </thead>
            <tbody>
              {d.gruende.map((g) => (
                <tr key={g.grund}>
                  <td>{g.grund}</td>
                  <td className="r zahl">{anzahl(g.anzahl)}</td>
                  <td className="r zahl">{euro(g.summe)}</td>
                  <td className="r zahl dezent">{anzahl(g.wiedereroeffnet)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="dezent" style={{ fontSize: 11, marginTop: 12, marginBottom: 0, lineHeight: 1.6 }}>
            Der Grund ist eine Auswahl aus einer Liste, kein Freitext. Er
            sagt, was der Standort als Grund vorsieht — nicht mit
            Sicherheit, was geschehen ist.
          </p>
        </Karte>

        <Karte titel="Stornos je Stunde" neben="Ortszeit des Standorts">
          <Balken
            werte={stundenLuecken(d.stunden,
              (h) => ({ stunde: h, anzahl: 0, summe: '0' })).map((s) => ({
              schluessel: String(s.stunde),
              etikett: String(s.stunde).padStart(2, '0'),
              wert: s.anzahl, neben: `${stunde(s.stunde)} · ${euro(s.summe)}`,
            }))}
            formatiere={(w) => `${anzahl(w)} Stornos`}
            formatiereAchse={(w) => anzahl(w)} />
          <p className="dezent" style={{ fontSize: 11, marginTop: 10, marginBottom: 0, lineHeight: 1.6 }}>
            Der Bericht führt die Zeit auf die Minute, nicht auf die
            Sekunde. Für den zeitlichen Ablauf eines einzelnen Belegs
            bleibt das Archiv zuständig.
          </p>
        </Karte>
      </div>

      <div className="gitter g-2" style={{ marginBottom: 14 }}>
        <Karte titel="Belege mit den meisten Stornos"
               neben={mitPersonen ? 'der Einstieg für die Prüfung'
                 : 'wo sich Stornos häufen'}>
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Beleg</th>
                  <th className="r">Stornos</th>
                  <th className="r">Summe</th>
                  <th className="r">Zeit</th>
                </tr>
              </thead>
              <tbody>
                {d.belege.map((b) => (
                  <tr key={`${b.betrieb_id}-${b.beleg}`}>
                    <td>
                      <span className="zahl">{b.beleg}</span>
                      {b.tisch && (
                        <span className="dezent" style={{ marginLeft: 7, fontSize: 11 }}>
                          Tisch {b.tisch}
                        </span>
                      )}
                      {mehrereStandorte && (
                        <span className="dezent" style={{ marginLeft: 7, fontSize: 11 }}>
                          {b.betrieb}
                        </span>
                      )}
                      {b.wiedereroeffnet > 0 && (
                        <span className="merker" style={{ marginLeft: 7 }}>
                          {b.wiedereroeffnet === b.stornos
                            ? 'wiedereröffnet'
                            : `${b.wiedereroeffnet}× wiedereröffnet`}
                        </span>
                      )}
                    </td>
                    <td className="r zahl">{anzahl(b.stornos)}</td>
                    <td className="r zahl">{euro(b.summe)}</td>
                    <td className="r zahl dezent">
                      {datumKurz(b.tag)} {b.von}
                      {b.bis !== b.von && `–${b.bis}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Karte>

        <Karte titel="Stornierte Artikel" neben="häufigste zuerst">
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th className="r">Anzahl</th>
                  <th className="r">Summe</th>
                </tr>
              </thead>
              <tbody>
                {d.artikel.map((a) => (
                  <tr key={a.artikel}>
                    <td>
                      {a.artikel}
                      {a.ohne_preis === a.anzahl && (
                        <span className="dezent" style={{ marginLeft: 7, fontSize: 11 }}>
                          ohne eigenen Preis
                        </span>
                      )}
                    </td>
                    <td className="r zahl">{anzahl(a.anzahl)}</td>
                    <td className="r zahl">{euro(a.summe)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Karte>
      </div>

      {!mitPersonen && (
        <p className="dezent" style={{ fontSize: 11, margin: 0, lineHeight: 1.6, maxWidth: '78ch' }}>
          Wer storniert hat, steht hier nicht — der Personenteil wird im
          Reporting nicht abgefragt. Er gehört zum Audit, und dort wird er
          auch geprüft.
        </p>
      )}
    </>
  )
}
